<template>
  <div class="integrations-view">
    <PageHeader
      title="Integraties"
      description="Koppel je tools en beheer hoe ze samenwerken met funnels en automatiseringen."
    >
      <template #actions>
        <Btn variant="primary" @click="handleAddIntegration">Integratie toevoegen</Btn>
      </template>
    </PageHeader>

    <div class="integrations-view__content">
      <div v-if="loading" class="integrations-view__loading">
        <span>Integraties laden...</span>
      </div>

      <template v-else>
        <section class="integrations-view__section">
          <div class="integrations-view__section-header">
            <h2 class="integrations-view__section-title">Agenda's</h2>
            <p class="integrations-view__section-description">
              Koppel Google of Outlook. Elk account is een aparte agenda, en je kunt er meerdere
              toevoegen.
            </p>
          </div>
          <div class="integrations-view__list">
            <OutlookIntegrationCard
              v-for="integration in googleIntegrations"
              :key="integration.id"
              provider="google"
              :integration="integration"
              :show-admin-consent="false"
              @manage="openCalendarManage(integration, 'google')"
            />
            <OutlookIntegrationCard
              provider="google"
              :adding-another="googleIntegrations.length > 0"
              :loading="googleConnecting"
              :show-admin-consent="false"
              @connect="connectCalendar('google')"
            />
            <OutlookIntegrationCard
              v-for="integration in outlookIntegrations"
              :key="integration.id"
              :integration="integration"
              :show-admin-consent="false"
              @manage="openCalendarManage(integration, 'outlook')"
            />
            <OutlookIntegrationCard
              :adding-another="outlookIntegrations.length > 0"
              :loading="outlookConnecting"
              :copying-admin-consent="copyingOutlookAdminConsent"
              @connect="connectCalendar('outlook')"
              @copy-admin-consent="copyOutlookAdminConsentLink"
            />
          </div>
        </section>

        <section class="integrations-view__section">
          <div class="integrations-view__section-header">
            <h2 class="integrations-view__section-title">Integraties</h2>
            <p class="integrations-view__section-description">
              Beheer al je actieve koppelingen op een plek.
            </p>
          </div>
          <div class="integrations-view__list">
            <ClaudeIntegrationCard @open="openClaudeGuide" />
            <MollieIntegrationCard
              v-if="hasMollieAvailability"
              :status="mollieStatus"
              :license-available="hasSlimInnen"
              :loading="mollieLoading"
              @connect="openMollieConnect"
              @manage="openMollieManage"
            />
            <ZoBoekhoudenIntegrationCard
              :integration="zoBoekhoudenIntegration"
              @manage="openZoBoekhoudenManage"
            />
            <SnelstartIntegrationCard
              :integration="snelstartIntegration"
              @manage="openSnelstartManage"
            />
            <TwinfieldIntegrationCard
              :integration="twinfieldIntegration"
              @manage="openTwinfieldManage"
            />
            <MoneybirdIntegrationCard
              :integration="moneybirdIntegration"
              @manage="openMoneybirdManage"
            />
            <IntegrationCard
              v-for="integration in filteredIntegrations"
              :key="integration.id"
              :integration="integration"
              @open-modal="handleOpenIntegration(integration)"
            />
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
  import { ref, computed, getCurrentInstance, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import IntegrationCard from '@/components/integrations/IntegrationCard.vue';
  import IntegrationSetupModal from '@/components/integrations/IntegrationSetupModal.vue';
  import SelectIntegrationModal from '@/components/integrations/SelectIntegrationModal.vue';
  import MollieIntegrationCard from '@/components/integrations/MollieIntegrationCard.vue';
  import MollieManageModal from '@/components/integrations/MollieManageModal.vue';
  import ZoBoekhoudenIntegrationCard from '@/components/integrations/ZoBoekhoudenIntegrationCard.vue';
  import ZoBoekhoudenManageModal from '@/components/integrations/ZoBoekhoudenManageModal.vue';
  import SnelstartIntegrationCard from '@/components/integrations/SnelstartIntegrationCard.vue';
  import SnelstartManageModal from '@/components/integrations/SnelstartManageModal.vue';
  import TwinfieldIntegrationCard from '@/components/integrations/TwinfieldIntegrationCard.vue';
  import TwinfieldManageModal from '@/components/integrations/TwinfieldManageModal.vue';
  import MoneybirdIntegrationCard from '@/components/integrations/MoneybirdIntegrationCard.vue';
  import MoneybirdManageModal from '@/components/integrations/MoneybirdManageModal.vue';
  import OutlookManageModal from '@/components/integrations/OutlookManageModal.vue';
  import OutlookIntegrationCard from '@/components/integrations/OutlookIntegrationCard.vue';
  import ClaudeGuideDrawer from '@/components/integrations/ClaudeGuideDrawer.vue';
  import ClaudeIntegrationCard from '@/components/integrations/ClaudeIntegrationCard.vue';
  import Btn from '@/components/partials/Btn.vue';
  import { useIntegrationStore } from '@/store/integrationStore';
  import { useUIStore } from '@/store/uiStore';
  import { useMollieStore } from '@/store/mollieStore';
  import { useInvoiceStore } from '@/store/invoiceStore';
  import { useToastStore } from '@/store/toastStore';
  import IntegrationService from '@/services/integrationService';

  export default {
    name: 'SettingsIntegrationsView',

    components: {
      PageHeader,
      IntegrationCard,
      IntegrationSetupModal,
      MollieIntegrationCard,
      ZoBoekhoudenIntegrationCard,
      SnelstartIntegrationCard,
      TwinfieldIntegrationCard,
      MoneybirdIntegrationCard,
      OutlookIntegrationCard,
      ClaudeIntegrationCard,
      Btn,
    },

    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const route = useRoute();
      const router = useRouter();
      const outlookConnecting = ref(false);
      const googleConnecting = ref(false);
      const copyingOutlookAdminConsent = ref(false);
      const loading = ref(false);
      const integrationStore = useIntegrationStore();
      const mollieStore = useMollieStore();
      const invoiceStore = useInvoiceStore();
      const toastStore = useToastStore();
      const integrations = computed(() => integrationStore.integrations);

      const hasSlimInnen = computed(() => invoiceStore.hasModuleSlimInnen);
      const mollieStatus = computed(() => mollieStore.status);
      const mollieLoading = computed(() => mollieStore.statusLoading);
      const hasMollieAvailability = computed(() => {
        // Always show the tile once the trigger-availability call has loaded
        // so customers can see *why* Mollie is hidden (license required) and
        // upgrade. Hiding it entirely makes the upsell invisible.
        return invoiceStore.triggerAvailabilityLoaded;
      });
      const HIDDEN_FROM_LIST = new Set([
        'mollie',
        'zo_boekhouden',
        'snelstart',
        'twinfield',
        'moneybird',
        'outlook_calendar',
        'google_calendar',
      ]);

      const zoBoekhoudenIntegration = computed(
        () => integrations.value.find((integration) => integration.type === 'zo_boekhouden') || null
      );

      const snelstartIntegration = computed(
        () => integrations.value.find((integration) => integration.type === 'snelstart') || null
      );

      const twinfieldIntegration = computed(
        () => integrations.value.find((integration) => integration.type === 'twinfield') || null
      );

      const moneybirdIntegration = computed(
        () => integrations.value.find((integration) => integration.type === 'moneybird') || null
      );

      const outlookIntegrations = computed(() =>
        integrations.value.filter((integration) => integration.type === 'outlook_calendar')
      );

      const googleIntegrations = computed(() =>
        integrations.value.filter((integration) => integration.type === 'google_calendar')
      );

      const filteredIntegrations = computed(() => {
        const filtered = integrations.value.filter(
          (integration) => !HIDDEN_FROM_LIST.has(integration.type)
        );

        return [...filtered].sort((a, b) => {
          // Put active integrations first
          if (a.is_active && !b.is_active) return -1;
          if (!a.is_active && b.is_active) return 1;
          // Then sort by name
          return a.name.localeCompare(b.name);
        });
      });

      const integrationOptions = computed(() =>
        Object.entries(integrationStore.integrationTypes)
          .filter(([id]) => !HIDDEN_FROM_LIST.has(id) || id === 'mollie')
          .map(([id, type]) => ({
            id,
            name: type.name,
            type: id,
          }))
      );

      // Fetch integrations
      const fetchIntegrations = async () => {
        loading.value = true;
        try {
          await integrationStore.fetchIntegrations();
        } catch (error) {
          console.error('Failed to fetch integrations:', error);
          toastStore.addToast({
            type: 'error',
            message: 'Kon integraties niet laden.',
          });
        } finally {
          loading.value = false;
        }
      };

      const mapOutlookError = (errorCode) => {
        if (errorCode === 'OUTLOOK_NOT_CONFIGURED') {
          return 'Outlook tijdelijk niet beschikbaar.';
        }
        if (errorCode) {
          return `Outlook koppeling mislukt (${errorCode}).`;
        }
        return 'Outlook koppeling mislukt.';
      };

      const consumeOutlookErrorQuery = async () => {
        const rawError = route.query?.error;
        if (!rawError) return false;
        const errorCode = Array.isArray(rawError) ? rawError[0] : rawError;
        toastStore.addToast({ type: 'error', message: mapOutlookError(errorCode) });
        const nextQuery = { ...route.query };
        delete nextQuery.error;
        await router.replace({ query: nextQuery });
        return true;
      };

      const waitForPopupClose = (popupWindow) =>
        new Promise((resolve) => {
          const timer = setInterval(() => {
            if (popupWindow.closed) {
              clearInterval(timer);
              resolve();
            }
          }, 400);
        });

      const copyOutlookAdminConsentLink = async () => {
        copyingOutlookAdminConsent.value = true;
        try {
          const response = await IntegrationService.getOutlookAdminConsentUrl();
          const url = response?.url;
          if (!url) {
            throw new Error('Geen admin consent link ontvangen.');
          }
          await navigator.clipboard.writeText(url);
          toastStore.addToast({
            type: 'success',
            message: 'Admin consent link gekopieerd.',
          });
        } catch (error) {
          const errorCode = error?.response?.data?.code;
          const status = error?.response?.status;
          if (status === 503 && errorCode === 'OUTLOOK_NOT_CONFIGURED') {
            toastStore.addToast({ type: 'error', message: 'Outlook tijdelijk niet beschikbaar.' });
          } else {
            const message =
              error?.response?.data?.error ||
              error.message ||
              'Kon de admin consent link niet kopiëren.';
            toastStore.addToast({ type: 'error', message });
          }
        } finally {
          copyingOutlookAdminConsent.value = false;
        }
      };

      const connectCalendar = async (provider) => {
        const isGoogle = provider === 'google';
        const type = isGoogle ? 'google_calendar' : 'outlook_calendar';
        const label = isGoogle ? 'Google' : 'Outlook';
        const connecting = isGoogle ? googleConnecting : outlookConnecting;
        const knownIds = new Set(
          integrations.value.filter((item) => item.type === type).map((item) => item.id)
        );

        connecting.value = true;
        try {
          const response = isGoogle
            ? await IntegrationService.connectGoogleCalendar()
            : await IntegrationService.connectOutlookCalendar(undefined, { popup: true });
          const url = response?.url;
          if (!url) {
            throw new Error('No OAuth URL returned');
          }

          const popup = window.open(
            url,
            `${type}-auth`,
            'width=620,height=760,resizable=yes,scrollbars=yes'
          );
          if (!popup) {
            window.location.assign(url);
            toastStore.addToast({
              type: 'warning',
              message: 'Popup geblokkeerd, doorgestuurd in hetzelfde venster.',
            });
            return;
          }

          await waitForPopupClose(popup);
          await fetchIntegrations();
          const created = integrations.value.find(
            (item) => item.type === type && !knownIds.has(item.id)
          );
          toastStore.addToast(
            created
              ? { type: 'success', message: `${label} succesvol gekoppeld.` }
              : { type: 'info', message: `${label}-koppeling geannuleerd.` }
          );
        } catch (error) {
          const errorCode = error?.response?.data?.code;
          const status = error?.response?.status;
          if (!isGoogle && status === 503 && errorCode === 'OUTLOOK_NOT_CONFIGURED') {
            toastStore.addToast({ type: 'error', message: 'Outlook tijdelijk niet beschikbaar.' });
          } else {
            const message =
              error?.response?.data?.error ||
              error.message ||
              `Kon ${label}-koppeling niet starten.`;
            toastStore.addToast({ type: 'error', message });
          }
        } finally {
          connecting.value = false;
        }
      };

      const openCalendarManage = async (selectedIntegration = null, provider = 'outlook') => {
        if (!selectedIntegration?.id) return;
        let integration = selectedIntegration;
        try {
          const hydrated = await integrationStore.ensureIntegrationDetails(selectedIntegration.id);
          if (hydrated) integration = hydrated;
        } catch (error) {
          console.error('Failed to hydrate calendar integration:', error);
        }

        const label = provider === 'google' ? 'Google Calendar' : 'Outlook Calendar';
        const modalId = uiStore.addModal(OutlookManageModal, {
          title: `Beheer ${label}`,
          size: 'md',
          provider,
          integration,
          onUpdated: async () => {
            await fetchIntegrations();
          },
        });
        uiStore.updateModalProps(modalId, { modalId });
      };

      const uiStore = useUIStore();

      const openClaudeGuide = () => {
        uiStore.openDrawer(ClaudeGuideDrawer);
      };
      // Handle adding new integration
      const handleAddIntegration = () => {
        const selectModalId = uiStore.addModal(SelectIntegrationModal, {
          title: 'Add integration',
          description: 'Select which integration you would like to add',
          options: integrationOptions.value,
          onSubmit: async (selectedType) => {
            uiStore.closeModal(selectModalId);

            const setupModalId = uiStore.addModal(IntegrationSetupModal, {
              title: `Setup ${selectedType.name}`,
              description: `Configure your ${selectedType.name} integration settings`,
              integration: {
                type: selectedType.type,
                name: selectedType.name,
                is_active: true,
                settings: {}, // Empty settings, modal will handle initialization
              },
              isEdit: false,
              onSubmit: async (formData) => {
                try {
                  // Split out formConnections from formData
                  const { formConnections, ...integrationData } = formData;

                  // Create integration with main data
                  const response = await integrationStore.createIntegration(
                    integrationData,
                    formConnections
                  );

                  if (response.status === 201) {
                    toastStore.addToast({
                      type: 'success',
                      message: `${selectedType.name} toegevoegd.`,
                    });
                    uiStore.closeModal(setupModalId);
                  }
                } catch (error) {
                  console.error('Failed to create integration:', error);
                  toastStore.addToast({
                    type: 'error',
                    message: `Kon ${selectedType.name} niet toevoegen.`,
                  });
                }
              },
            });
          },
        });
      };

      const handleOpenIntegration = (integration) => {
        if (integration.type === 'outlook_calendar' || integration.type === 'google_calendar') {
          openCalendarManage(
            integration,
            integration.type === 'google_calendar' ? 'google' : 'outlook'
          );
          return;
        }
        openSettingsModal(integration);
      };

      // Handle opening settings modal for existing integration
      const openSettingsModal = (integration) => {
        const setupModalId = uiStore.addModal(IntegrationSetupModal, {
          title: `Edit ${integration.name}`,
          description: `Configure your ${integration.name} integration settings`,
          integration: integration,
          isEdit: true,
          onSubmit: async (formData) => {
            try {
              const response = await integrationStore.updateIntegration(integration.id, formData);
              if (response.status === 200) {
                toastStore.addToast({
                  type: 'success',
                  message: `${integration.name} bijgewerkt.`,
                });
                uiStore.closeModal(setupModalId);
              }
            } catch (error) {
              console.error('Failed to update integration:', error);
              toastStore.addToast({
                type: 'error',
                message: `Kon ${integration.name} niet bijwerken.`,
              });
            }
          },
          onDeleteIntegration: async (integrationId) => {
            try {
              await integrationStore.deleteIntegration(integrationId);
              toastStore.addToast({
                type: 'success',
                message: `${integration.name} verwijderd.`,
              });
              uiStore.closeModal(setupModalId);
            } catch (error) {
              console.error('Failed to delete integration:', error);
              toastStore.addToast({
                type: 'error',
                message: `Kon ${integration.name} niet verwijderen.`,
              });
            }
          },
        });
      };

      const openMollieManage = () => {
        const modalId = uiStore.addModal(MollieManageModal, {
          title: $t('mollie.actions.manage'),
          size: 'md',
        });
        // Pass the modalId back so the inner modal can self-close after a
        // successful disconnect.
        uiStore.updateModalProps(modalId, { modalId });
      };

      const openZoBoekhoudenManage = () => {
        const modalId = uiStore.addModal(ZoBoekhoudenManageModal, {
          title: $t('zo_boekhouden.actions.manage'),
          size: 'md',
          integrationId: zoBoekhoudenIntegration.value?.id || null,
          onUpdated: async () => {
            await fetchIntegrations();
          },
        });
        uiStore.updateModalProps(modalId, { modalId });
      };

      const openSnelstartManage = () => {
        const modalId = uiStore.addModal(SnelstartManageModal, {
          title: $t('snelstart.actions.manage'),
          size: 'md',
          onUpdated: async () => {
            await fetchIntegrations();
          },
        });
        uiStore.updateModalProps(modalId, { modalId });
      };

      const openTwinfieldManage = () => {
        const modalId = uiStore.addModal(TwinfieldManageModal, {
          title: $t('twinfield.actions.manage'),
          size: 'md',
          onUpdated: async () => {
            await fetchIntegrations();
          },
        });
        uiStore.updateModalProps(modalId, { modalId });
      };

      const openMoneybirdManage = () => {
        const modalId = uiStore.addModal(MoneybirdManageModal, {
          title: $t('moneybird.actions.manage'),
          size: 'md',
          onUpdated: async () => {
            await fetchIntegrations();
          },
        });
        uiStore.updateModalProps(modalId, { modalId });
      };

      const openMollieConnect = async () => {
        if (!hasSlimInnen.value) {
          toastStore.addToast({ type: 'info', message: $t('mollie.license_required') });
          return;
        }
        try {
          const result = await mollieStore.startOAuth();
          await Promise.all([
            mollieStore.fetchStatus({ force: true }),
            invoiceStore.fetchTriggerAvailability(true),
          ]);
          if (result?.success) {
            toastStore.addToast({ type: 'success', message: $t('mollie.connect.success') });
          } else if (result?.error === 'popup_closed') {
            toastStore.addToast({ type: 'info', message: $t('mollie.connect.cancelled') });
          } else if (result?.error === 'popup_blocked') {
            toastStore.addToast({ type: 'warning', message: $t('mollie.connect.popup_blocked') });
          } else {
            toastStore.addToast({ type: 'error', message: $t('mollie.connect.failed') });
          }
        } catch (error) {
          const msg = error?.response?.data?.error || error.message || $t('mollie.connect.failed');
          toastStore.addToast({ type: 'error', message: msg });
        }
      };

      onMounted(async () => {
        await fetchIntegrations();
        const hadOutlookError = await consumeOutlookErrorQuery();
        if (hadOutlookError) {
          await fetchIntegrations();
        }
        // Trigger-availability is the source of truth for the Slim Innen
        // license check; load it here so the Mollie tile renders correctly
        // even when the user lands directly on this page.
        await invoiceStore.fetchTriggerAvailability();
        if (hasSlimInnen.value) {
          // Best-effort fetch of the deeper Mollie status. Failure is fine —
          // we still render the tile based on the slim summary in trigger-
          // availability.
          try {
            await mollieStore.fetchStatus();
          } catch {
            /* surfaced via toast on click */
          }
        }
      });

      return {
        loading,
        integrations,
        outlookConnecting,
        googleConnecting,
        copyingOutlookAdminConsent,
        outlookIntegrations,
        googleIntegrations,
        filteredIntegrations,
        openClaudeGuide,
        handleAddIntegration,
        handleOpenIntegration,
        openSettingsModal,
        connectCalendar,
        openCalendarManage,
        copyOutlookAdminConsentLink,
        hasSlimInnen,
        hasMollieAvailability,
        mollieStatus,
        mollieLoading,
        openMollieConnect,
        openMollieManage,
        snelstartIntegration,
        openSnelstartManage,
        twinfieldIntegration,
        openTwinfieldManage,
        moneybirdIntegration,
        openMoneybirdManage,
        zoBoekhoudenIntegration,
        openZoBoekhoudenManage,
      };
    },
  };
</script>

<style scoped lang="scss">
  .integrations-view {
    &__content {
      margin-top: 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    &__section {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &__section-header {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.75rem;
      flex-wrap: wrap;
    }

    &__section-title {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__section-description {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 3rem;
      color: var(--slate-500);
    }

    &__list {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;

      @include lg-down {
        grid-template-columns: 1fr;
      }
    }
  }
</style>
